import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const SettingsPage = () => {
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const [username, setUsername] = useState(user?.username || "");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
      console.error("CSRF token or param meta tag not found!");
      return { token: null, param: null };
    }

    return {
      token: csrfTokenTag.getAttribute("content"),
      param: csrfParamTag.getAttribute("content"),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setMessage("You must be logged in to update your username.");
      setIsError(true);
      return;
    }

    try {
      const { token } = getCsrfToken();
      if (!token) {
        setMessage("CSRF token not found.");
        setIsError(true);
        return;
      }

      const response = await fetch(`/api/users/${user.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify({ user: { username } }),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser); // Update user in context
        setMessage("Username updated successfully!");
        setIsError(false);
        navigate("/games"); // Redirect after successful update
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || "Failed to update username.");
        setIsError(true);
      }
    } catch (error) {
      console.error("Error updating username:", error);
      setMessage("An unexpected error occurred.");
      setIsError(true);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Settings</h1>
        <p>Please log in to view your settings.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Settings</h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="username">Username</Label>
          <Input
            id="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            className="mt-1"
          />
        </div>
        <Button type="submit">Update Username</Button>
      </form>
      {message && (
        <p className={`mt-4 ${isError ? "text-red-500" : "text-green-500"}`}>
          {message}
        </p>
      )}
    </div>
  );
};
